import { searchParamsData } from '@/lib/sbAPI'
import { getStoryblokApi } from '@/lib/storyblok-api'

interface StoryblokStory {
  id: string
  name: string
  full_slug: string
  content: Record<string, unknown>
  [key: string]: unknown
}

interface StoryblokHeaders extends Headers {
  total: number
}

interface FetchOfficesOptions {
  officesUUIDs?: string
  location?: string
  filterLocation?: string
  paginate?: boolean
  page?: number
  perPage?: number
}

export const fetchOffices = async ({
  officesUUIDs = '',
  location = 'au',
  filterLocation = 'en-nz',
  paginate = false,
  page = 1,
  perPage = 50,
}: FetchOfficesOptions) => {
  const storyblokApi = getStoryblokApi()
  let allOffices: StoryblokStory[] = []

  try {
    if (paginate) {
      // Single page fetch for paginated results
      const response = await storyblokApi.get('cdn/stories', {
        ...searchParamsData,
        resolve_relations:
          'article.author,article.relatedArticles,business_story.author,relatedContent.items,service.relatedArticles,adviser.offices,office.advisers,office.relatedAdvisers',
        filter_query: {
          component: { in: 'advanceOffice,office' },
          __or: [
            { 'settings.location_available': { in: location } },
            { 'settings.location_available': { not_in: filterLocation } },
          ],
        },
        by_uuids: officesUUIDs,
        excluding_fields:
          'title,mailingAddress,officeHours,settings,customAdviserOrder,customAdvisers',
        per_page: perPage,
        page,
        sort_by: 'name:asc',
      })

      return {
        offices: response.data.stories,
        total: Number((response.headers as StoryblokHeaders).total ?? 0),
        currentPage: page,
      }
    } else {
      // Fetch all pages
      let hasMorePages = true
      let currentPage = 1

      while (hasMorePages) {
        const response = await storyblokApi.get('cdn/stories', {
          ...searchParamsData,
          resolve_relations:
            'article.author,article.relatedArticles,business_story.author,relatedContent.items,service.relatedArticles,adviser.offices,office.advisers,office.relatedAdvisers',
          filter_query: {
            component: { in: 'advanceOffice,office' },
            __or: [
              { 'settings.location_available': { in: location } },
              { 'settings.location_available': { not_in: filterLocation } },
            ],
          },
          by_uuids: officesUUIDs,
          excluding_fields:
            'title,mailingAddress,officeHours,settings,customAdviserOrder,customAdvisers',
          per_page: perPage,
          page: currentPage,
          sort_by: 'name:asc',
        })

        allOffices = [...allOffices, ...response.data.stories]

        const total = Number((response.headers as StoryblokHeaders).total ?? 0)
        const currentCount = currentPage * perPage
        hasMorePages = currentCount < total
        currentPage++
      }

      return { offices: allOffices }
    }
  } catch (error) {
    console.error('Error fetching office locations:', error)
    throw error
  }
}
